/* /audit — audit log (owner): search, area filter, CSV export. */
import "./pay.css";
import { useMemo, useState } from "react";
import { useAudit } from "../../data/hooks";
import { useI18n } from "../../i18n";
import { csvOf, downloadText } from "../../lib/format";
import { Button, EmptyState, ErrorBox, PageHeader, SearchBox, Select, Skeleton, useToast } from "../../ui";

const AREA_AR: Record<string, string> = { PayrollRun: "الرواتب", Employee: "الموظف", Settings: "الإعدادات", Loan: "القروض", Letter: "الخطابات", Expense: "المصروفات", Leave: "الإجازات", Asset: "الأصول", Offboarding: "إنهاء الخدمة", Onboarding: "المباشرة", File: "الملفات", AIFeature: "الذكاء الاصطناعي", Device: "الأجهزة" };

export default function Audit() {
  const { L, t, isAr } = useI18n();
  const toast = useToast();
  const q = useAudit();
  const [search, setSearch] = useState("");
  const [area, setArea] = useState("");
  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (q.data ?? []).filter((r) => (!area || r.entity === area) && (!s || [r.actor, r.action, r.entity, r.entityId, r.detail].some((x) => x.toLowerCase().includes(s))));
  }, [q.data, search, area]);
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Skeleton />;
  const areas = [...new Set(q.data.map((r) => r.entity))].sort();
  const areaLabel = (a: string) => (isAr ? AREA_AR[a] ?? a : a);
  const exportCsv = () => {
    downloadText("Audit_log.csv", csvOf([["When", "Who", "Action", "Area", "Entity ID", "Detail"], ...rows.map((r) => [r.at, r.actor, r.action, r.entity, r.entityId, r.detail])]));
    toast(L("Audit log exported · " + rows.length + " entries", "تم تصدير السجل · " + rows.length + " سجل"));
  };
  return (
    <>
      <PageHeader title={L("Audit log", "سجل التدقيق")} sub={L("Every change, export and sensitive-field view — who, what, when and from where.", "كل تغيير وتصدير وعرض للحقول الحساسة — من وماذا ومتى ومن أين.")}
        actions={<Button icon="download" onClick={exportCsv} disabled={!rows.length} data-testid="btn-export-audit">{t("Export")}</Button>} />
      <section className="card">
        <div className="toolbar">
          <SearchBox value={search} onChange={setSearch} placeholder={t("Search") + "…"} testId="audit-search" />
          <Select aria-label={L("Area", "المجال")} value={area} onChange={(e) => setArea(e.target.value)} options={[["", L("All areas", "كل المجالات")], ...areas.map((a): [string, string] => [a, areaLabel(a)])]} data-testid="audit-area" />
          <span className="spacer" /><span className="xs muted num" data-testid="audit-count">{rows.length} / {q.data.length}</span>
        </div>
        {rows.length === 0 ? <EmptyState title={q.data.length ? L("No matching entries", "لا توجد نتائج مطابقة") : L("No activity yet", "لا يوجد نشاط بعد")} desc={L("Changes made in StartHR appear here automatically.", "تظهر التغييرات في ستارت HR هنا تلقائيًا.")} /> : (
          <div className="tw">
            <table className="tbl cards compact" data-testid="audit-table">
              <thead><tr><th>{L("When", "الوقت")}</th><th>{L("Who", "المستخدم")}</th><th>{L("Area", "المجال")}</th><th>{L("What", "الإجراء")}</th><th className="hide-md">{L("Detail", "التفاصيل")}</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={"audit-row-" + r.id}>
                    <td className="x num xs" data-l="" dir="ltr">{r.at.replace("T", " ").slice(0, 16)}</td>
                    <td className="k1"><b className="sm">{r.actor}</b></td>
                    <td className="k2"><span className="chip">{areaLabel(r.entity)}</span></td>
                    <td className="k3" data-l="">{r.action}{r.entityId && <span className="xs muted"> · {r.entityId}</span>}</td>
                    <td className="hide-md num xs">{r.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
